import React from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import {
  Lock as LockIcon,
  ContactSupport as ContactIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

interface PermissionRequiredProps {
  resource: string;
  action: string;
  message?: string;
  showContactAdmin?: boolean;
  variant?: "alert" | "card" | "inline";
}

/**
 * Component to display when user lacks required permission
 * Shows helpful message with contact information
 */
export const PermissionRequired: React.FC<PermissionRequiredProps> = ({
  resource,
  action,
  message,
  showContactAdmin = true,
  variant = "alert",
}) => {
  // Finnish translations
  const actionLabels: Record<string, string> = {
    create: "luontioikeus",
    read: "lukuoikeus",
    update: "muokkausoikeus",
    delete: "poisto-oikeus",
  };

  const resourceLabels: Record<string, string> = {
    invoice: "laskutukseen",
    meeting: "kokouksiin",
    user: "käyttäjiin",
    association: "yhdistykseen",
    unit: "yksikointiin",
    property: "kiinteistöihin",
    file: "tiedostoihin",
    voucher: "tositteisiin",
    account: "tileihin",
    road: "teihin",
    membership: "jäsenyyksiin",
  };

  const actionText = actionLabels[action] || action;
  const resourceText = resourceLabels[resource] || resource;

  const defaultMessage =
    message ||
    `Sinulla ei ole ${actionText} ${resourceText}. Ota yhteyttä tiekunnan ylläpitäjään, jos tarvitset käyttöoikeuksia.`;

  if (variant === "inline") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1,
          px: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
        }}
      >
        <LockIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {defaultMessage}
        </Typography>
      </Box>
    );
  }

  if (variant === "card") {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          px: 3,
        }}
      >
        <LockIcon
          sx={{
            fontSize: 64,
            color: "action.disabled",
            mb: 2,
          }}
        />
        <Typography variant="h6" gutterBottom color="text.secondary">
          Käyttöoikeus puuttuu
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {defaultMessage}
        </Typography>
        {showContactAdmin && (
          <Button
            component={RouterLink}
            to="/contact"
            variant="outlined"
            startIcon={<ContactIcon />}
            sx={{ mt: 2 }}
          >
            Ota yhteyttä
          </Button>
        )}
      </Box>
    );
  }

  // Default: alert variant
  return (
    <Alert
      severity="warning"
      icon={<LockIcon />}
      sx={{ mb: 2 }}
      action={
        showContactAdmin ? (
          <Button
            component={RouterLink}
            to="/contact"
            size="small"
            startIcon={<ContactIcon />}
          >
            Yhteys
          </Button>
        ) : undefined
      }
    >
      <Typography variant="body2" fontWeight="medium">
        Käyttöoikeus puuttuu
      </Typography>
      <Typography variant="body2">{defaultMessage}</Typography>
    </Alert>
  );
};
